// src/pages/admin/CourseLectures/components/LectureUI.tsx
import React from "react";

// ── Section wrapper ──────────────────────────────────────────────
export const Section: React.FC<{
  num: string;
  icon: string;
  title: string;
  children: React.ReactNode;
  highlight?: boolean;
}> = ({ num, icon, title, children, highlight = false }) => (
  <div
    className={`bg-white rounded-xl border mb-5 overflow-hidden shadow-sm ${
      highlight ? "border-[#009fe3] border-2" : "border-gray-200"
    }`}
  >
    <div
      className={`flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 ${
        highlight
          ? "bg-gradient-to-r from-blue-50 to-white"
          : "bg-gradient-to-r from-[#f0f9ff] to-white"
      }`}
    >
      <div className="w-9 h-9 rounded-lg bg-[#009fe3] text-white flex items-center justify-center text-base flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[10px] font-bold text-[#009fe3] bg-blue-50 px-2 py-0.5 rounded-full inline-block mb-0.5 uppercase tracking-wide">
          {num}
        </div>
        <h2 className="text-base font-bold text-gray-900 leading-tight">
          {title}
        </h2>
      </div>
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

// ── Checklist ────────────────────────────────────────────────────
export const Checklist: React.FC<{ items: (string | React.ReactNode)[] }> = ({
  items,
}) => (
  <ul className="space-y-0">
    {items.map((item, i) => (
      <li
        key={i}
        className="flex items-start gap-2.5 py-2 border-b border-gray-50 last:border-0 text-sm text-gray-700"
      >
        <span className="w-[18px] h-[18px] rounded-full bg-green-100 text-green-600 text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
          ✓
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

// ── Info box ─────────────────────────────────────────────────────
export const InfoBox: React.FC<{
  icon?: string;
  title?: string;
  children: React.ReactNode;
  variant?: "warning" | "info" | "tip";
}> = ({ icon, title, children, variant = "warning" }) => {
  const styles = {
    warning: "bg-amber-50 border-amber-300 text-amber-800",
    info: "bg-blue-50 border-blue-300 text-blue-800",
    tip: "bg-green-50 border-green-300 text-green-800",
  };
  return (
    <div
      className={`border-l-4 rounded-r-lg px-4 py-3 mt-3 text-sm leading-relaxed ${styles[variant]}`}
    >
      {title && (
        <div className="font-bold mb-1">
          {icon} {title}
        </div>
      )}
      {children}
    </div>
  );
};

// ── Agenda chips ─────────────────────────────────────────────────
export const AgendaCard: React.FC<{
  items: { num: string; label: string }[];
}> = ({ items }) => (
  <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 mb-5 shadow-sm">
    <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">
      📋 Agenda dieser Lektion
    </h3>
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <div
          key={item.num}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 hover:border-[#009fe3] hover:text-[#009fe3] hover:bg-blue-50 cursor-pointer transition-all"
        >
          <span className="w-[17px] h-[17px] rounded-full bg-[#009fe3] text-white text-[9px] font-bold flex items-center justify-center">
            {item.num}
          </span>
          {item.label}
        </div>
      ))}
    </div>
  </div>
);

// ── File naming row ───────────────────────────────────────────────
export const FileNamingGrid: React.FC<{
  items: {
    label: string;
    codes: string[];
    note?: string;
    fullWidth?: boolean;
  }[];
}> = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
    {items.map((item, i) => (
      <div
        key={i}
        className={`bg-gray-50 border border-gray-200 rounded-lg p-3 ${
          item.fullWidth ? "sm:col-span-2" : ""
        }`}
      >
        <div className="text-[10px] font-bold text-[#009fe3] uppercase tracking-wide mb-2">
          {item.label}
        </div>
        {item.codes.map((c, j) => (
          <code
            key={j}
            className="block font-mono text-[11px] text-gray-800 bg-gray-200 px-2 py-1 rounded mb-1.5"
          >
            {c}
          </code>
        ))}
        {item.note && (
          <p className="text-[11px] text-gray-400 mt-1">{item.note}</p>
        )}
      </div>
    ))}
  </div>
);

// ── Task list ────────────────────────────────────────────────────
// FIX: accepts string | ReactNode so JSX items (e.g. links) work without TS errors
export const TaskList: React.FC<{ tasks: (string | React.ReactNode)[] }> = ({
  tasks,
}) => (
  <ul className="space-y-0">
    {tasks.map((task, i) => (
      <li
        key={i}
        className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0 text-sm text-gray-700"
      >
        <span className="w-6 h-6 rounded-full bg-[#009fe3] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <span>{task}</span>
      </li>
    ))}
  </ul>
);

// ── Photo tips grid ───────────────────────────────────────────────
export const PhotoTips: React.FC<{
  tips: { emoji: string; label: string }[];
}> = ({ tips }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2">
    {tips.map((t, i) => (
      <div
        key={i}
        className="flex items-center gap-2.5 p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm font-medium text-[#0369a1]"
      >
        <span className="text-xl flex-shrink-0">{t.emoji}</span>
        {t.label}
      </div>
    ))}
  </div>
);

// ── Email example row ─────────────────────────────────────────────
export const EmailExample: React.FC<{
  address: string;
  tag: string;
  tagColor?: string;
}> = ({ address, tag, tagColor = "bg-blue-100 text-[#0369a1]" }) => (
  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 mb-1.5 text-sm">
    <code className="font-mono text-[#0369a1] text-[13px]">{address}</code>
    <span
      className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${tagColor}`}
    >
      {tag}
    </span>
  </div>
);

// ── Tool chips row ────────────────────────────────────────────────
export const ToolChips: React.FC<{
  tools: { label: string; href?: string }[];
}> = ({ tools }) => (
  <div className="flex flex-wrap gap-2 mt-3">
    {tools.map((t, i) => (
      <div
        key={i}
        className="px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700"
      >
        {t.href ? (
          <a
            href={t.href}
            target="_blank"
            rel="noreferrer"
            className="text-[#009fe3] hover:underline"
          >
            {t.label}
          </a>
        ) : (
          t.label
        )}
      </div>
    ))}
  </div>
);

// ── Education comparison table ────────────────────────────────────
// FIX: wrapped in overflow-x-auto to prevent layout breaks on narrow screens
export const EduTable: React.FC<{
  rows: { input: string; output: string }[];
}> = ({ rows }) => (
  <div className="overflow-x-auto -mx-1 mt-2">
    <table className="w-full text-sm border-collapse min-w-[320px]">
      <thead>
        <tr>
          <th className="bg-[#009fe3] text-white px-4 py-2 text-left font-semibold rounded-tl-lg">
            Dein Abschluss
          </th>
          <th className="bg-[#009fe3] text-white px-2 py-2 w-8"></th>
          <th className="bg-[#009fe3] text-white px-4 py-2 text-left font-semibold rounded-tr-lg">
            Deutsches Äquivalent
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="hover:bg-blue-50 transition-colors">
            <td className="px-4 py-2.5 border-b border-gray-100">
              {row.input}
            </td>
            <td className="px-2 py-2.5 border-b border-gray-100 text-[#009fe3] font-bold text-center">
              →
            </td>
            <td className="px-4 py-2.5 border-b border-gray-100 font-medium text-gray-800">
              {row.output}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Lecture progress bar ──────────────────────────────────────────
export const LectureProgress: React.FC<{
  current: number;
  total: number;
}> = ({ current, total }) => {
  const pct = Math.round(((current - 1) / total) * 100);
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="flex-1 bg-gray-200 rounded-full h-1.5 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#009fe3] to-[#0088cc] rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-gray-400 whitespace-nowrap">
        {current} / {total} Lektionen
      </span>
    </div>
  );
};
